import { ComponentChildren } from 'preact'
import { memo } from 'preact/compat'
import { useState, useEffect } from 'preact/hooks'
import { bgPositionToValue } from 'core/helpers/styles'
import type { Column as ColumnType } from 'embed_v2/types/version'

type Props = {
  id: ColumnType['id']
  title: ColumnType['title']
  options: ColumnType['options']
  width: number
  columnGap: number
  columnsCount: number
  children: ComponentChildren
}

const Column = (props: Props) => {
  const { id, title, options } = props

  const defaultWidth = `calc(${props.width}% - calc(${props.columnGap}px / ${props.columnsCount} * ${props.columnsCount - 1}))`

  const [width, setWidth] = useState(defaultWidth)

  useEffect(() => {
    const listenWidth = (x: MediaQueryList | MediaQueryListEvent) => {
      if (x.matches) {
        setWidth('100%')
      } else {
        setWidth(defaultWidth)
      }
    }

    const x = window.matchMedia('(max-width: 770px)')
    listenWidth(x)
    x.addEventListener('change', listenWidth)

    return () => {
      x.removeEventListener('change', listenWidth)
    }
  }, [id])

  const styles = {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    flex: '0 0 auto',
    flexBasis: width,
    maxWidth: width,
    backgroundRepeat: 'no-repeat',
    backgroundColor: `rgba(${options.bg_color})`,
    backgroundImage: options.bg_image_url ? `url(${options.bg_image_url})` : '',
    backgroundPosition:
      options.bg_image_position === 'advanced'
        ? options.bg_image_position_advanced
        : bgPositionToValue(options.bg_image_position),
    backgroundSize: options.bg_image_size === 'advanced' ? options.bg_image_size_advanced : options.bg_image_size,
    marginTop: `${options.margin_top}px`,
    paddingTop: `${options.padding_top}px`,
    paddingRight: `${options.padding_right}px`,
    paddingBottom: `${options.padding_bottom}px`,
    paddingLeft: `${options.padding_left}px`,
    borderTop: options.border_side[0]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderRight: options.border_side[1]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderBottom: options.border_side[2]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderLeft: options.border_side[3]
      ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
      : 'none',
    borderTopLeftRadius: `${options.border_top_left}px`,
    borderTopRightRadius: `${options.border_top_right}px`,
    borderBottomRightRadius: `${options.border_bottom_left}px`,
    borderBottomLeftRadius: `${options.border_bottom_right}px`,
    boxShadow: options.box_shadow
  }

  return (
    <div id={`wf_column_${id}`} className={`wf_column wf_column_${id} wf_reset`} style={styles} data-title={title}>
      {props.children}
    </div>
  )
}

export default memo(Column)
